import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, MoveRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { scrollToSection } from './Navbar';

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: '115%' }}
      animate={{ y: '0%' }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 140]);
  const yBadge = useTransform(scrollY, [0, 800], [0, -80]);
  const opacityHint = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden grain"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 75% 40%, rgba(212,175,55,0.08), transparent 65%), radial-gradient(ellipse 50% 60% at 15% 85%, rgba(212,175,55,0.05), transparent 60%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 w-full grid lg:grid-cols-12 gap-14 items-center relative">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-12 bg-[#D4AF37]/60" />
            <p className="kicker" data-testid="hero-kicker">
              Jakson Faucets &amp; Accessories — 15 Years of Excellence
            </p>
          </motion.div>

          <h1 className="font-serif font-light tracking-tight leading-[0.98] text-[13vw] sm:text-7xl lg:text-[6.2rem]" data-testid="hero-heading">
            <MaskedLine delay={0.35}>Redefining</MaskedLine>
            <MaskedLine delay={0.5} className="italic text-gold-gradient">
              Water.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="mt-8 max-w-md text-base sm:text-lg text-zinc-400 font-light leading-relaxed"
            data-testid="hero-subtext"
          >
            Sense a better freshness. Faucets and bath accessories cast in solid brass,
            finished like jewellery, and built in Bhiwadi, Rajasthan — for homes,
            hotels and spaces that refuse the ordinary.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-explore-btn"
              onClick={() => navigate('/collections')}
              className="group flex items-center gap-3 bg-[#D4AF37] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-[#F5E6AD] transition-colors duration-300"
            >
              Explore Collections
              <MoveRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
            <button
              data-testid="hero-atelier-btn"
              onClick={() => scrollToSection('#configurator')}
              className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-300 border border-zinc-700 px-8 py-4 hover:border-[#D4AF37] hover:text-[#F5E6AD] transition-colors duration-300"
            >
              Finish Atelier
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-md"
            data-testid="hero-stats"
          >
            {[
              ['15+', 'Years of craft'],
              ['31', 'Collections'],
              ['720+', 'Products'],
            ].map(([n, l]) => (
              <div key={l} className="border-l border-[#D4AF37]/25 pl-4">
                <p className="font-serif text-3xl text-[#F5E6AD]">{n}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-[#D4AF37]/25 pointer-events-none" />
            <motion.div style={{ y: yImg }} className="overflow-hidden bg-black">
              <img
                src="/assets/img/jsp-3613.jpg"
                alt="JSP-3613 Pillar Cock Extended Body"
                data-testid="hero-product-image"
                className="w-full h-[420px] sm:h-[540px] object-contain animate-floaty"
              />
            </motion.div>
            <motion.img
              src="/assets/img/badge_15.jpg"
              alt="15 years of excellence"
              data-testid="hero-badge"
              style={{ y: yBadge }}
              initial={{ opacity: 0, rotate: -8 }}
              animate={{ opacity: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 1.1 }}
              className="absolute -top-8 -left-8 w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-full border border-[#D4AF37]/40 shadow-[0_0_40px_rgba(212,175,55,0.2)]"
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
              className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 text-right"
            >
              JSP-3613 — Pillar Cock, Extended Body
            </motion.p>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        style={{ opacity: opacityHint }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        onClick={() => scrollToSection('#configurator')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-500 hover:text-[#D4AF37] transition-colors"
        aria-label="Scroll down"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};
